import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Trash2 } from "lucide-react";
import { API_URL } from "../../lib/utils";
import { EXEMPTION_REASONS } from "../../lib/fiscal";

const emptyLine = () => ({ product_name: "", quantity: 1, unit_price: 0, tax_rate: 14, tax_exemption_reason: "" });

export const ManualRecoveryDialog = ({ open, onOpenChange, onDone }) => {
  const [form, setForm] = useState({ doc_type: "FR", manual_series: "", manual_number: "", manual_date: "", customer_name: "", customer_nif: "", payment_method: "NU" });
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);

  const setLine = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const total = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0) * (1 + Number(l.tax_rate || 0) / 100), 0);

  const submit = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API_URL}/invoices/manual-recovery`, { ...form, lines: lines.map((l) => ({ ...l, tax_exemption_reason: l.tax_exemption_reason || null })) }, { withCredentials: true });
      toast.success(`Documento manual recuperado como ${r.data.invoice_number}`);
      onDone?.(r.data);
      onOpenChange(false);
      setLines([emptyLine()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao recuperar documento manual");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="manual-recovery-dialog">
        <DialogHeader>
          <DialogTitle>Recuperar documento manual</DialogTitle>
          <DialogDescription>Documentos emitidos em tipografia durante uma falha do sistema (Anexo I n.º 16). Ficam na série M, com SourceBilling = M e referência ao original.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label>Tipo</Label>
            <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
              <SelectTrigger data-testid="manual-doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="FT">FT — Factura</SelectItem>
                <SelectItem value="FR">FR — Factura-Recibo</SelectItem>
                <SelectItem value="NC">NC — Nota de Crédito</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label>Série manual</Label><Input value={form.manual_series} onChange={(e) => setForm({ ...form, manual_series: e.target.value })} placeholder="A" data-testid="manual-series-input" /></div>
          <div className="space-y-1"><Label>N.º manual</Label><Input value={form.manual_number} onChange={(e) => setForm({ ...form, manual_number: e.target.value })} placeholder="000123" data-testid="manual-number-input" /></div>
          <div className="space-y-1"><Label>Data do documento</Label><Input type="date" value={form.manual_date} onChange={(e) => setForm({ ...form, manual_date: e.target.value })} data-testid="manual-date-input" /></div>
          <div className="space-y-1 col-span-2"><Label>Cliente</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Consumidor final" /></div>
          <div className="space-y-1"><Label>NIF cliente</Label><Input value={form.customer_nif} onChange={(e) => setForm({ ...form, customer_nif: e.target.value })} placeholder="999999999" /></div>
          <div className="space-y-1">
            <Label>Pagamento</Label>
            <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NU">Numerário</SelectItem>
                <SelectItem value="CD">Cartão / TPA</SelectItem>
                <SelectItem value="TB">Transferência</SelectItem>
                <SelectItem value="OU">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between"><Label>Linhas</Label><Button size="sm" variant="ghost" onClick={() => setLines([...lines, emptyLine()])} className="gap-1"><Plus className="w-3 h-3" /> Linha</Button></div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-4"><Input value={l.product_name} onChange={(e) => setLine(i, { product_name: e.target.value })} placeholder="Descrição" data-testid={`manual-line-name-${i}`} /></div>
              <div className="col-span-2"><Input type="number" min="0.01" step="0.01" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} placeholder="Qtd" /></div>
              <div className="col-span-2"><Input type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} placeholder="Preço" data-testid={`manual-line-price-${i}`} /></div>
              <div className="col-span-1"><Input type="number" min="0" step="1" value={l.tax_rate} onChange={(e) => setLine(i, { tax_rate: e.target.value })} placeholder="IVA" /></div>
              <div className="col-span-2">
                {Number(l.tax_rate) === 0 ? (
                  <Select value={l.tax_exemption_reason} onValueChange={(v) => setLine(i, { tax_exemption_reason: v })}>
                    <SelectTrigger><SelectValue placeholder="Motivo" /></SelectTrigger>
                    <SelectContent>{EXEMPTION_REASONS.map((r) => <SelectItem key={r.code} value={r.code}>{r.code}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <span className="text-xs text-muted-foreground">—</span>}
              </div>
              <div className="col-span-1"><Button size="icon" variant="ghost" onClick={() => setLines(lines.filter((_, j) => j !== i))} disabled={lines.length === 1}><Trash2 className="w-4 h-4" /></Button></div>
            </div>
          ))}
          <p className="text-right text-sm font-mono">Total c/ IVA: {total.toFixed(2)} Kz</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving || !form.manual_series || !form.manual_number || !form.manual_date || lines.some((l) => !l.product_name)} data-testid="manual-recovery-submit">
            {saving ? "A registar…" : "Recuperar documento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
